/**
 * Admin — Digital Menu management for one outlet.
 *
 * Flow:
 *   1. Toggle "Enable Digital Menu" (auto-generates a menu_slug).
 *   2. Create menu entries (Food, Beverage, ...) with menu_type dropdown.
 *   3. Upload PDF → auto-parses via Claude → shows parsed preview.
 *   4. Admin can edit parsed items inline, then Publish.
 *   5. QR panel shows a printable branded card pointing at the OUTLET slug.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { QrDialog } from '../components/menu/QrDialog';
import {
  ArrowLeft, Upload, RefreshCw, Trash2, Eye, Send, Archive, Download,
  Printer, QrCode, FilePlus2, ChevronDown, ChevronUp, Sparkles, AlertTriangle,
  ArrowUp, ArrowDown, Plus, Save,
} from 'lucide-react';

const PRIMARY = 'var(--color-primary)';
const SECONDARY = '#C9A227';
const CREAM = '#FBF7EC';

export default function AdminOutletMenus() {
  const { outletId } = useParams();
  const navigate = useNavigate();
  const { API, getHeaders } = useAuth();
  const [config, setConfig] = useState(null);
  const [menus, setMenus] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewMenu, setPreviewMenu] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, typesRes, cfgRes] = await Promise.all([
        axios.get(`${API}/admin/outlets/${outletId}/menus`, { headers: getHeaders() }),
        axios.get(`${API}/admin/menu-types`, { headers: getHeaders() }),
        axios.get(`${API}/admin/outlets/${outletId}/menu-config`, { headers: getHeaders() }),
      ]);
      setConfig({
        outlet: listRes.data.outlet,
        menu_enabled: listRes.data.menu_enabled,
        menu_slug: listRes.data.menu_slug,
        // iter280 — Menu Studio owns the welcome message.
        menu_welcome_message: cfgRes.data?.menu_welcome_message || '',
      });
      setMenus(listRes.data.menus || []);
      setTypes(typesRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders, outletId]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (enabled) => {
    try {
      const res = await axios.patch(
        `${API}/admin/outlets/${outletId}/menu-config`,
        { enabled }, { headers: getHeaders() },
      );
      setConfig((c) => ({ ...c, ...res.data }));
      toast.success(enabled ? 'Digital menu enabled' : 'Digital menu disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  // iter280 — Menu Studio: save the landing welcome message.
  const [welcomeSaving, setWelcomeSaving] = useState(false);
  const saveWelcomeMessage = async () => {
    setWelcomeSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/outlets/${outletId}/menu-config`,
        { menu_welcome_message: config?.menu_welcome_message || '' },
        { headers: getHeaders() },
      );
      setConfig((c) => ({ ...c, ...res.data }));
      toast.success('Menu welcome message saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setWelcomeSaving(false);
    }
  };

  const openQr = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/outlets/${outletId}/menu-qr?request_url=${encodeURIComponent(window.location.origin)}`,
        { headers: getHeaders() },
      );
      setQrData(res.data);
      setQrOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'QR generation failed');
    }
  };

  if (loading) {
    return <div className="p-10 text-center text-gray-500">Loading…</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-outlet-menus">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/outlets')}
                  className="mb-2 -ml-2 text-gray-500">
            <ArrowLeft className="w-4 h-4 mr-1" />Back to outlets
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: PRIMARY }}>
            Menu Studio · {config?.outlet?.name}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Upload PDFs, publish menus, and share one QR that always shows the latest content.
          </p>
        </div>
      </div>

      {/* Enable + slug */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-base" style={{ color: PRIMARY }}>Enable Digital Menu</div>
              <div className="text-xs text-gray-500">
                When OFF, the public URL returns <span className="font-mono">Menu Coming Soon</span>.
              </div>
            </div>
            <Switch
              checked={!!config?.menu_enabled}
              onCheckedChange={toggleEnabled}
              data-testid="menu-enabled-toggle"
            />
          </div>

          {config?.menu_slug && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
              <span className="text-gray-500">Public URL:</span>
              <span className="font-mono bg-gray-50 px-2 py-1 rounded border" data-testid="menu-public-url">
                /menu/{config.menu_slug}
              </span>
              <div className="flex gap-2 sm:ml-auto">
                <Button variant="outline" size="sm" onClick={openQr}
                        data-testid="show-qr-btn">
                  <QrCode className="w-4 h-4 mr-1" />QR Code
                </Button>
                <Link to={`/menu/${config.menu_slug}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4 mr-1" />View Menu
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Menu Config — iter280 — Menu Studio owns menu-scoped fields. */}
      <Card data-testid="menu-config-card">
        <CardHeader className="pb-2">
          <CardTitle style={{ color: PRIMARY }}>Menu Config</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0 space-y-3">
          <Label className="text-sm font-medium" htmlFor="menu-welcome-input">
            Menu Landing Welcome Message
          </Label>
          <Textarea id="menu-welcome-input" rows={2}
                    value={config?.menu_welcome_message || ''}
                    onChange={(e) => setConfig((c) => ({
                      ...c, menu_welcome_message: e.target.value.slice(0, 500),
                    }))}
                    placeholder="Explore our carefully crafted menu"
                    data-testid="menu-welcome-message-input" />
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-gray-500">
              Shown on the guest menu landing page. Falls back to a default
              if left blank. ({(config?.menu_welcome_message || '').length}/500)
            </div>
            <Button size="sm" onClick={saveWelcomeMessage} disabled={welcomeSaving}
                    style={{ background: PRIMARY, color: '#fff' }}
                    data-testid="menu-welcome-message-save">
              {welcomeSaving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Menus list */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle style={{ color: PRIMARY }}>Menus</CardTitle>
          <Button size="sm" onClick={() => setCreateOpen(true)}
                  style={{ background: PRIMARY, color: '#fff' }}
                  data-testid="add-menu-btn">
            <FilePlus2 className="w-4 h-4 mr-1" />New Menu
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {menus.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">
              No menus yet. Create one (e.g. &ldquo;Food Menu&rdquo; or &ldquo;Beverage Menu&rdquo;) to get started.
            </div>
          ) : (
            <div className="divide-y">
              {menus.map((m) => (
                <MenuRow
                  key={m.id}
                  menu={m}
                  types={types}
                  API={API}
                  headers={getHeaders}
                  expanded={!!expanded[m.id]}
                  onToggleExpand={() => setExpanded((e) => ({ ...e, [m.id]: !e[m.id] }))}
                  onPreview={() => setPreviewMenu(m)}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <CreateMenuDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        types={types}
        API={API}
        headers={getHeaders}
        outletId={outletId}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      {/* ITER287 — Menu Editor Studio (replaces the read-only preview) */}
      {previewMenu && (
        <MenuEditorStudio
          menu={previewMenu}
          API={API}
          headers={getHeaders}
          onClose={() => setPreviewMenu(null)}
          onSaved={load}
        />
      )}

      {/* QR dialog */}
      <QrDialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        data={qrData}
        outletName={config?.outlet?.name}
      />
    </div>
  );
}

/* ==================== Menu row ==================== */

function MenuRow({ menu, types, API, headers, expanded, onToggleExpand, onPreview, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Footer Notes — inline editor lives directly on the Menu Studio
  // expanded row (NOT in the preview modal). See iter281 fix.
  const [footerNotes, setFooterNotes] = useState(menu.footer_notes || '');
  const [footerSaving, setFooterSaving] = useState(false);
  const [footerDirty, setFooterDirty] = useState(false);

  const typeLabel = (types.find((t) => t.code === menu.menu_type)?.label) || menu.menu_type;

  // Re-sync when parent reloads the row (e.g. after publish/parse).
  useEffect(() => {
    setFooterNotes(menu.footer_notes || '');
    setFooterDirty(false);
  }, [menu.id, menu.footer_notes]);

  const saveFooterNotes = async () => {
    setFooterSaving(true);
    try {
      await axios.put(
        `${API}/admin/menus/${menu.id}`,
        { footer_notes: footerNotes },
        { headers: headers() },
      );
      toast.success('Footer notes saved');
      setFooterDirty(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save footer notes');
    } finally {
      setFooterSaving(false);
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      await axios.post(
        `${API}/admin/menus/${menu.id}/upload-pdf`,
        form,
        { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('PDF uploaded — parsing in background');
      onChanged();
      pollParseStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const pollParseStatus = () => {
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/admin/menus/${menu.id}`,
                                  { headers: headers() });
        const st = r.data.parse_status;
        if (st === 'ok') {
          toast.success('Menu parsed successfully');
          onChanged(); return;
        }
        if (st === 'failed') {
          toast.error(`Parse failed: ${r.data.parse_error || 'unknown error'}`);
          onChanged(); return;
        }
        if (attempts < 90) setTimeout(poll, 2000);  // 3 minutes total
        else onChanged();
      } catch { /* silent */ }
    };
    setTimeout(poll, 2000);
  };

  const doAction = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/menus/${menu.id}/${action}`, {}, { headers: headers() });
      if (action === 'parse') {
        toast.info('Retrying parse in background…');
        onChanged();
        pollParseStatus();
      } else {
        toast.success('Updated');
        onChanged();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm('Delete this menu? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/menus/${menu.id}`, { headers: headers() });
      toast.success('Menu deleted');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // Derive a single unified status chip.
  // Priority: parsing > parse_failed > incomplete > (archived|published|ready|draft)
  let chip;
  if (menu.parse_status === 'parsing') {
    chip = { label: 'Parsing',      cls: 'bg-amber-50 text-amber-700 border border-amber-200',
             icon: <RefreshCw className="w-3 h-3 animate-spin" /> };
  } else if (menu.parse_status === 'failed') {
    chip = { label: 'Parse Failed', cls: 'bg-rose-50 text-rose-700 border border-rose-200' };
  } else if (menu.parse_status === 'incomplete') {
    chip = { label: 'Parse Incomplete', cls: 'bg-orange-50 text-orange-700 border border-orange-200' };
  } else if (menu.status === 'archived') {
    chip = { label: 'Archived',     cls: 'bg-amber-100 text-amber-800 border-transparent' };
  } else if (menu.status === 'published') {
    chip = { label: 'Published',    cls: 'bg-emerald-100 text-emerald-800 border-transparent' };
  } else if (menu.has_pdf && menu.parse_status === 'ok') {
    chip = { label: 'Ready',        cls: 'bg-emerald-50 text-emerald-700 border border-emerald-200' };
  } else if (!menu.has_pdf) {
    chip = { label: 'No PDF',       cls: 'bg-gray-100 text-gray-600 border-transparent' };
  } else {
    chip = { label: 'Draft',        cls: 'bg-gray-100 text-gray-700 border-transparent' };
  }

  const md = menu.pdf_metadata || {};
  const canPublish = menu.parse_status === 'ok';

  return (
    <div className="p-4" data-testid={`menu-row-${menu.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium" style={{ color: PRIMARY }}>{menu.name}</span>
            <Badge variant="outline" className="capitalize">{typeLabel}</Badge>
            <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 font-medium ${chip.cls}`}
                  data-testid={`chip-${menu.id}`}>
              {chip.icon}{chip.label}
            </span>
            <span className="text-xs text-gray-400">v{menu.version}</span>
            {menu.parse_status === 'failed' && menu.parse_error && (
              <span className="text-xs text-rose-500 truncate max-w-xs"
                    title={menu.parse_error}
                    data-testid={`parse-failed-${menu.id}`}>
                — {menu.parse_error}
              </span>
            )}
          </div>

          {/* Completeness dashboard row (#3) */}
          {md.page_count > 0 && (
            <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-600 flex-wrap"
                 data-testid={`completeness-${menu.id}`}>
              <span>Pages <b>{md.page_count}/{md.page_count}</b></span>
              <span>·</span>
              <span>Categories <b>{menu.categories_count}</b></span>
              <span>·</span>
              <span>Items <b>{md.parsed_total ?? menu.items_count}</b>/{md.expected_total || '—'}</span>
              <span>·</span>
              <span>Confidence <b style={{
                color: md.confidence_pct >= 90 ? '#2D6A4F' :
                       md.confidence_pct >= 70 ? '#B7791F' : '#B91C1C',
              }}>{md.confidence_pct != null ? `${md.confidence_pct}%` : '—'}</b></span>
              {md.complete === false && (
                <span className="inline-flex items-center gap-1 text-orange-700 font-medium">
                  ⚠ Incomplete — missing {(md.pages || []).reduce((s, p) => s + (p.missing || 0), 0)} items
                </span>
              )}
              {md.complete === true && (
                <span className="text-emerald-700 font-medium">✓ Complete</span>
              )}
            </div>
          )}

          <div className="text-xs text-gray-400 mt-1">
            Updated {menu.updated_at?.slice(0, 10) || '—'}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <label className="cursor-pointer">
            <input type="file" accept="application/pdf" className="hidden"
                   onChange={(e) => uploadPdf(e.target.files?.[0])}
                   data-testid={`upload-pdf-${menu.id}`} />
            <span className="inline-flex items-center gap-1 rounded-md border px-3 h-9 text-sm hover:bg-gray-50">
              <Upload className="w-4 h-4" />{menu.has_pdf ? 'Replace PDF' : 'Upload PDF'}
            </span>
          </label>

          {menu.has_pdf && (menu.parse_status === 'failed' || menu.parse_status === 'incomplete') && (
            <Button variant="outline" size="sm"
                    onClick={() => doAction('parse')} disabled={busy || uploading}
                    className={menu.parse_status === 'failed'
                      ? 'border-rose-300 text-rose-700 hover:bg-rose-50'
                      : 'border-orange-300 text-orange-700 hover:bg-orange-50'}
                    data-testid={`retry-parse-${menu.id}`}>
              <RefreshCw className="w-4 h-4 mr-1" />Retry Parse
            </Button>
          )}
          {menu.has_pdf && menu.parse_status === 'ok' && (
            <Button variant="outline" size="sm"
                    onClick={() => doAction('parse')} disabled={busy || uploading}
                    data-testid={`reparse-${menu.id}`}>
              <RefreshCw className="w-4 h-4 mr-1" />Re-parse
            </Button>
          )}

          <Button variant="outline" size="sm" onClick={onPreview}
                  disabled={menu.items_count === 0}
                  data-testid={`preview-${menu.id}`}>
            <Eye className="w-4 h-4 mr-1" />Edit Menu
          </Button>

          {menu.status !== 'published' && (
            <Button size="sm" onClick={() => doAction('publish')}
                    disabled={busy || !canPublish}
                    title={canPublish ? '' :
                      'Publishing is blocked until parse status is Complete'}
                    style={{ background: canPublish ? PRIMARY : '#9ca3af',
                             color: '#fff' }}
                    data-testid={`publish-${menu.id}`}>
              <Send className="w-4 h-4 mr-1" />Publish
            </Button>
          )}
          {menu.status === 'published' && (
            <Button variant="outline" size="sm" onClick={() => doAction('unpublish')}
                    disabled={busy}>
              Unpublish
            </Button>
          )}
          {menu.status !== 'archived' && (
            <Button variant="ghost" size="sm" onClick={() => doAction('archive')} disabled={busy}>
              <Archive className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={del}>
            <Trash2 className="w-4 h-4 text-rose-500" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onToggleExpand} data-testid={`expand-menu-${menu.id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t space-y-4" data-testid={`menu-editor-inline-${menu.id}`}>
          {menu.has_pdf && (
            <div className="pl-1 text-xs text-gray-500 flex flex-wrap gap-3">
              <span>PDF: <span className="font-mono">{menu.pdf_filename}</span></span>
              <a href={`${API}/admin/menus/${menu.id}/pdf`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                 onClick={(e) => {
                   e.preventDefault();
                   fetch(`${API}/admin/menus/${menu.id}/pdf`, { headers: headers() })
                     .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), '_blank'))
                     .catch(() => toast.error('Failed to open PDF'));
                 }}>
                <Download className="w-3 h-3" />Original PDF
              </a>
            </div>
          )}

          {/* iter281 — Footer Notes editor lives INLINE on the menu row,
              NOT inside a modal. Renders at the bottom of every guest
              menu category page. */}
          <div className="space-y-2" data-testid={`footer-notes-editor-${menu.id}`}>
            <Label className="text-sm font-semibold" style={{ color: PRIMARY }}>
              Footer Notes (visible at bottom of every menu page)
            </Label>
            <div className="text-xs text-gray-500">
              Statutory or legal text — VAT/GST, allergens, disclaimers.
            </div>
            <Textarea rows={3}
                      value={footerNotes}
                      onChange={(e) => {
                        setFooterNotes(e.target.value.slice(0, 1000));
                        setFooterDirty(true);
                      }}
                      placeholder="e.g. VAT of 20% applicable on all alcoholic beverages. Some preparations may contain allergens — please inform our team."
                      data-testid={`footer-notes-input-${menu.id}`} />
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-gray-400"
                   data-testid={`footer-notes-count-${menu.id}`}>
                {footerNotes.length}/1000 characters
              </div>
              <Button size="sm" onClick={saveFooterNotes}
                      disabled={footerSaving || !footerDirty}
                      style={{ background: PRIMARY, color: '#fff' }}
                      data-testid={`footer-notes-save-${menu.id}`}>
                {footerSaving ? 'Saving…'
                  : footerDirty ? 'Save Footer Notes' : 'Saved'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== Create dialog ==================== */

function CreateMenuDialog({ open, onClose, types, API, headers, outletId, onCreated }) {
  const [form, setForm] = useState({ name: '', menu_type: 'food', sort_order: 100 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ name: '', menu_type: 'food', sort_order: 100 }); }, [open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/outlets/${outletId}/menus`,
        form,
        { headers: headers() },
      );
      toast.success('Menu created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-menu-dialog">
        <DialogHeader>
          <DialogTitle>Create Menu</DialogTitle>
          <DialogDescription>
            Add a new menu (e.g. Food, Beverage). You can upload its PDF after creation.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Menu Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Bell Tower Food Menu"
                   data-testid="new-menu-name" />
          </div>
          <div>
            <Label>Menu Type</Label>
            <Select value={form.menu_type}
                    onValueChange={(v) => setForm({ ...form, menu_type: v })}>
              <SelectTrigger data-testid="new-menu-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sort Order</Label>
            <Input type="number" value={form.sort_order}
                   onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 100 })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                  style={{ background: PRIMARY, color: '#fff' }}
                  data-testid="save-menu-btn">
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== Menu Editor Studio (ITER287) ==================== */
/* Full editing UI. Replaces the read-only preview dialog. Holds a local
   `draft` of parsed_data, tracks a `dirty` flag, and only writes on the
   explicit "Save Changes" action. The "Mark Sold Out (shift/indefinite)"
   flow still uses the existing availability endpoint for daily-summary
   alerts; the Studio adds a simple "Enabled" toggle for permanent
   enable/disable inside the parsed_data payload. */

function makeItemId() {
  return (crypto?.randomUUID?.() || `it_${Math.random().toString(36).slice(2, 10)}`);
}
function makeCatId() {
  return (crypto?.randomUUID?.() || `c_${Math.random().toString(36).slice(2, 10)}`);
}

const VEG_OPTS = [
  { value: 'veg',     label: 'Veg' },
  { value: 'non_veg', label: 'Non-Veg' },
  { value: 'egg',     label: 'Contains Egg' },
  { value: 'unknown', label: 'Unclassified' },
];

function MenuEditorStudio({ menu, API, headers, onClose, onSaved }) {
  const [full, setFull] = useState(null);
  const [draft, setDraft] = useState(null);      // { categories: [...] }
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openCatId, setOpenCatId] = useState(null);
  const [openItemId, setOpenItemId] = useState(null);
  const [unavailModal, setUnavailModal] = useState(null);

  const reload = () => {
    axios.get(`${API}/admin/menus/${menu.id}`, { headers: headers() })
      .then((r) => {
        setFull(r.data);
        setDraft(JSON.parse(JSON.stringify(r.data.parsed_data || { categories: [] })));
        setDirty(false);
      })
      .catch(() => toast.error('Failed to load menu'));
  };
  useEffect(() => { reload(); }, [menu.id]);

  const mutate = (fn) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      fn(next);
      return next;
    });
    setDirty(true);
  };

  /* ── Category-level ops ─────────────────────────────────────────── */
  const addCategory = () =>
    mutate((d) => {
      d.categories.push({
        id: makeCatId(),
        name: 'New Category',
        description: '',
        sort_order: d.categories.length,
        items: [],
      });
    });
  const renameCategory = (cid, name) =>
    mutate((d) => { const c = d.categories.find(x => x.id === cid); if (c) c.name = name; });
  const editCategoryDesc = (cid, description) =>
    mutate((d) => { const c = d.categories.find(x => x.id === cid); if (c) c.description = description; });
  const deleteCategory = (cid) => {
    const c = draft.categories.find(x => x.id === cid);
    if (!c) return;
    if ((c.items || []).length > 0 && !window.confirm(
      `Delete "${c.name}" and its ${c.items.length} items? This cannot be undone until you Save Changes.`
    )) return;
    mutate((d) => { d.categories = d.categories.filter(x => x.id !== cid); });
  };
  const moveCategory = (cid, dir) =>
    mutate((d) => {
      const idx = d.categories.findIndex(x => x.id === cid);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= d.categories.length) return;
      [d.categories[idx], d.categories[j]] = [d.categories[j], d.categories[idx]];
    });

  /* ── Item-level ops ─────────────────────────────────────────────── */
  const addItem = (cid) =>
    mutate((d) => {
      const c = d.categories.find(x => x.id === cid);
      if (!c) return;
      c.items = c.items || [];
      c.items.push({
        id: makeItemId(),
        name: 'New Item',
        description: '',
        price: 0,
        price_display: '',
        veg: 'unknown',
        veg_manual_override: false,
        chef_recommend: false,
        bestseller: false,
        subcategory: '',
        available: true,
        tags: [],
        sort_order: c.items.length,
      });
    });
  const editItem = (cid, iid, patch) =>
    mutate((d) => {
      const c = d.categories.find(x => x.id === cid);
      const it = c?.items?.find(x => x.id === iid);
      if (it) Object.assign(it, patch);
    });
  const setVegOverride = (cid, iid, veg) =>
    editItem(cid, iid, { veg, veg_manual_override: true });
  const deleteItem = (cid, iid) =>
    mutate((d) => {
      const c = d.categories.find(x => x.id === cid);
      if (c) c.items = (c.items || []).filter(x => x.id !== iid);
    });
  const moveItem = (cid, iid, dir) =>
    mutate((d) => {
      const c = d.categories.find(x => x.id === cid);
      if (!c) return;
      const idx = c.items.findIndex(x => x.id === iid);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= c.items.length) return;
      [c.items[idx], c.items[j]] = [c.items[j], c.items[idx]];
    });
  const moveItemToCategory = (fromCid, iid, toCid) =>
    mutate((d) => {
      if (fromCid === toCid) return;
      const src = d.categories.find(x => x.id === fromCid);
      const dst = d.categories.find(x => x.id === toCid);
      if (!src || !dst) return;
      const idx = src.items.findIndex(x => x.id === iid);
      if (idx < 0) return;
      const [it] = src.items.splice(idx, 1);
      dst.items = dst.items || [];
      dst.items.push(it);
    });

  /* ── Save ───────────────────────────────────────────────────────── */
  const saveAll = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/menus/${menu.id}`,
        { parsed_data: draft },
        { headers: headers() },
      );
      toast.success('Menu saved');
      setDirty(false);
      onSaved && onSaved();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const requestClose = () => {
    if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  /* ── Shift-level Mark Sold Out (uses existing availability API) ─── */
  const submitUnavailable = async ({ mode, reason }) => {
    if (!unavailModal) return;
    try {
      await axios.patch(
        `${API}/admin/menus/${menu.id}/items/${unavailModal.itemId}/availability`,
        { available: false, unavailable_mode: mode, unavailable_reason: reason },
        { headers: headers() },
      );
      toast.success(mode === 'shift'
        ? 'Marked unavailable for this shift (auto-restores in 12h)'
        : 'Marked unavailable indefinitely');
      setUnavailModal(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update availability');
    }
  };

  const md = full?.pdf_metadata || {};
  const totalItems = (draft?.categories || []).reduce(
    (s, c) => s + (c.items || []).length, 0);

  return (
    <Dialog open={true} onOpenChange={(o) => !o && requestClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
                     data-testid="editor-menu-dialog">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4 w-full">
            <div>
              <DialogTitle style={{ color: PRIMARY }}>
                Menu Editor · {menu.name}
                {menu.parse_model && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-gray-500">
                    <Sparkles className="w-3 h-3" />parsed by {menu.parse_model}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {(draft?.categories || []).length} categories · {totalItems} items ·
                {dirty ? (
                  <span className="ml-1 inline-flex items-center gap-1 text-amber-700 font-medium"
                        data-testid="unsaved-indicator">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Unsaved changes
                  </span>
                ) : (
                  <span className="ml-1 text-emerald-700">All changes saved</span>
                )}
              </DialogDescription>
            </div>
            <Button size="sm" onClick={saveAll}
                    disabled={!dirty || saving}
                    style={{ background: dirty ? PRIMARY : '#9ca3af', color: '#fff' }}
                    data-testid="editor-save-btn">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Saving…' : dirty ? 'Save Changes' : 'Saved'}
            </Button>
          </div>
        </DialogHeader>

        {!full || !draft ? (
          <div className="p-6 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="flex-1 overflow-y-auto pr-1 space-y-4"
               data-testid="editor-body">
            {/* Completeness banner (retained from preview) */}
            {md.page_count > 0 && (
              <div className={`rounded-lg p-3 text-xs border ${
                md.complete
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-orange-50 border-orange-200 text-orange-900'
              }`} data-testid="completeness-banner">
                <div className="font-medium">
                  {md.complete ? '✓ Parse Complete' : '⚠ Parse Incomplete'}
                  {' '}· Pages {md.page_count}/{md.page_count} · Items {md.parsed_total}/{md.expected_total} · Confidence {md.confidence_pct}%
                </div>
                {!md.complete && (
                  <div className="text-orange-800 mt-1">
                    Publishing is blocked until parse is complete — you can still edit items here.
                  </div>
                )}
              </div>
            )}

            {(draft.categories || []).length === 0 && (
              <div className="text-center text-gray-400 py-10">
                No categories yet. Add one below or upload a PDF.
              </div>
            )}

            {(draft.categories || []).map((c, cIdx) => (
              <EditorCategoryBlock
                key={c.id}
                cat={c} cIdx={cIdx}
                totalCats={draft.categories.length}
                isOpen={openCatId === c.id}
                onToggle={() => setOpenCatId(openCatId === c.id ? null : c.id)}
                openItemId={openItemId}
                setOpenItemId={setOpenItemId}
                allCategories={draft.categories}
                onRename={renameCategory}
                onEditDesc={editCategoryDesc}
                onDelete={deleteCategory}
                onMove={moveCategory}
                onAddItem={addItem}
                onEditItem={editItem}
                onSetVegOverride={setVegOverride}
                onDeleteItem={deleteItem}
                onMoveItem={moveItem}
                onMoveItemToCategory={moveItemToCategory}
                onOpenUnavailModal={(itemId, itemName) =>
                  setUnavailModal({ itemId, itemName })}
              />
            ))}

            <Button variant="outline" size="sm" onClick={addCategory}
                    data-testid="editor-add-category">
              <Plus className="w-4 h-4 mr-1" />Add Category
            </Button>
          </div>
        )}
      </DialogContent>

      {unavailModal && (
        <UnavailableModal
          itemName={unavailModal.itemName}
          onClose={() => setUnavailModal(null)}
          onSubmit={submitUnavailable}
        />
      )}
    </Dialog>
  );
}

function EditorCategoryBlock({
  cat, cIdx, totalCats, isOpen, onToggle, openItemId, setOpenItemId,
  allCategories, onRename, onEditDesc, onDelete, onMove,
  onAddItem, onEditItem, onSetVegOverride, onDeleteItem,
  onMoveItem, onMoveItemToCategory, onOpenUnavailModal,
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white"
         data-testid={`editor-cat-${cat.id}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50/60">
        <button type="button" onClick={onToggle}
                className="p-1 rounded hover:bg-black/5">
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4 rotate-90" />}
        </button>
        <input value={cat.name}
               onChange={(e) => onRename(cat.id, e.target.value.slice(0, 120))}
               className="flex-1 min-w-0 bg-transparent text-sm font-semibold outline-none"
               style={{ color: PRIMARY }}
               data-testid={`editor-cat-name-${cat.id}`} />
        <span className="text-[10px] text-gray-400">{(cat.items || []).length} items</span>
        <button type="button" onClick={() => onMove(cat.id, -1)}
                disabled={cIdx === 0}
                className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                data-testid={`editor-cat-up-${cat.id}`}
                title="Move category up">
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => onMove(cat.id, +1)}
                disabled={cIdx === totalCats - 1}
                className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                data-testid={`editor-cat-down-${cat.id}`}
                title="Move category down">
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => onDelete(cat.id)}
                className="p-1 rounded hover:bg-rose-50"
                data-testid={`editor-cat-delete-${cat.id}`}
                title="Delete category">
          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
        </button>
      </div>

      {isOpen && (
        <div className="p-3 space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">
              Category description (optional)
            </Label>
            <Input value={cat.description || ''}
                   onChange={(e) => onEditDesc(cat.id, e.target.value.slice(0, 400))}
                   placeholder="Shown as tagline under the category name"
                   data-testid={`editor-cat-desc-${cat.id}`}
                   className="h-8 text-sm mt-1" />
          </div>

          {(cat.items || []).length === 0 ? (
            <div className="text-xs text-gray-400 italic py-2">No items in this category.</div>
          ) : (
            <div className="space-y-2">
              {cat.items.map((it, iIdx) => (
                <EditorItemBlock
                  key={it.id}
                  cat={cat} item={it} iIdx={iIdx}
                  totalItems={cat.items.length}
                  isOpen={openItemId === it.id}
                  onToggle={() => setOpenItemId(openItemId === it.id ? null : it.id)}
                  allCategories={allCategories}
                  onEditItem={onEditItem}
                  onSetVegOverride={onSetVegOverride}
                  onDeleteItem={onDeleteItem}
                  onMoveItem={onMoveItem}
                  onMoveItemToCategory={onMoveItemToCategory}
                  onOpenUnavailModal={onOpenUnavailModal}
                />
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => onAddItem(cat.id)}
                  data-testid={`editor-add-item-${cat.id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" />Add Item
          </Button>
        </div>
      )}
    </div>
  );
}

function EditorItemBlock({
  cat, item, iIdx, totalItems, isOpen, onToggle, allCategories,
  onEditItem, onSetVegOverride, onDeleteItem, onMoveItem,
  onMoveItemToCategory, onOpenUnavailModal,
}) {
  const it = item;
  const soldOut = it.available === false;
  return (
    <div className={`rounded border ${soldOut ? 'bg-rose-50/30 border-rose-200' : 'border-gray-200'}`}
         data-testid={`editor-item-${it.id}`}>
      {/* Summary row */}
      <div className="flex items-center gap-2 px-2.5 py-2">
        <button type="button" onClick={onToggle}
                className="p-1 rounded hover:bg-black/5">
          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5 rotate-90" />}
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-medium truncate ${soldOut ? 'line-through' : ''}`}>
            {it.name || <span className="text-gray-400 italic">Unnamed</span>}
          </span>
          <VegDot type={it.veg} />
          {it.veg_manual_override && (
            <span className="text-[9px] px-1 rounded bg-blue-50 text-blue-700 border border-blue-200"
                  title="Dietary manually overridden — parser re-runs will not overwrite this">
              MANUAL
            </span>
          )}
          {it.chef_recommend && (
            <Badge variant="outline" className="text-[9px] text-amber-700 border-amber-400 h-4 px-1">
              Recommended
            </Badge>
          )}
          {it.bestseller && (
            <Badge variant="outline" className="text-[9px] text-rose-700 border-rose-400 h-4 px-1">
              Bestseller
            </Badge>
          )}
          {soldOut && (
            <span className="text-[9px] px-1.5 rounded-full bg-rose-100 text-rose-700 font-medium">
              Sold Out{it.unavailable_mode ? ` · ${it.unavailable_mode}` : ''}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 tabular-nums">
          {it.price_display || (it.price ? `₹${it.price}` : '—')}
        </div>
        <button type="button" onClick={() => onMoveItem(cat.id, it.id, -1)}
                disabled={iIdx === 0}
                className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                data-testid={`editor-item-up-${it.id}`}
                title="Move up">
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => onMoveItem(cat.id, it.id, +1)}
                disabled={iIdx === totalItems - 1}
                className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                data-testid={`editor-item-down-${it.id}`}
                title="Move down">
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => onDeleteItem(cat.id, it.id)}
                className="p-1 rounded hover:bg-rose-50"
                data-testid={`editor-item-delete-${it.id}`}
                title="Delete item">
          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
        </button>
      </div>

      {/* Expanded editor */}
      {isOpen && (
        <div className="border-t px-3 py-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-white">
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Item Name</Label>
            <Input value={it.name}
                   onChange={(e) => onEditItem(cat.id, it.id, { name: e.target.value.slice(0, 200) })}
                   className="h-8 text-sm mt-1"
                   data-testid={`editor-item-name-${it.id}`} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Description</Label>
            <Textarea rows={2}
                      value={it.description || ''}
                      onChange={(e) => onEditItem(cat.id, it.id, { description: e.target.value.slice(0, 600) })}
                      className="text-sm mt-1"
                      data-testid={`editor-item-desc-${it.id}`} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Price (₹)</Label>
            <Input type="number" min="0" step="1"
                   value={it.price || 0}
                   onChange={(e) => onEditItem(cat.id, it.id, {
                     price: Number(e.target.value) || 0,
                     price_display: '',
                   })}
                   className="h-8 text-sm mt-1"
                   data-testid={`editor-item-price-${it.id}`} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">
              Display Price (optional)
            </Label>
            <Input value={it.price_display || ''}
                   onChange={(e) => onEditItem(cat.id, it.id, { price_display: e.target.value.slice(0, 40) })}
                   placeholder="e.g. ₹250 / ₹450"
                   className="h-8 text-sm mt-1"
                   data-testid={`editor-item-price-display-${it.id}`} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Sub-Category</Label>
            <Input value={it.subcategory || ''}
                   onChange={(e) => onEditItem(cat.id, it.id, { subcategory: e.target.value.slice(0, 80) })}
                   placeholder="e.g. Small Plates, Cold Brews"
                   className="h-8 text-sm mt-1"
                   data-testid={`editor-item-subcat-${it.id}`} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Move to Category</Label>
            <Select value={cat.id}
                    onValueChange={(v) => onMoveItemToCategory(cat.id, it.id, v)}>
              <SelectTrigger className="h-8 text-sm mt-1" data-testid={`editor-item-move-${it.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allCategories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">
              Dietary Classification
              {it.veg_manual_override && (
                <span className="ml-1 text-[9px] text-blue-700">
                  (manual override — parser re-runs will not overwrite)
                </span>
              )}
            </Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {VEG_OPTS.map((opt) => {
                const active = it.veg === opt.value;
                return (
                  <button key={opt.value} type="button"
                          onClick={() => onSetVegOverride(cat.id, it.id, opt.value)}
                          data-testid={`editor-item-veg-${it.id}-${opt.value}`}
                          className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                            active
                              ? 'bg-slate-900 text-white border-slate-900'
                              : 'bg-white border-gray-300 text-gray-700 hover:border-slate-500'
                          }`}>
                    {opt.label}
                  </button>
                );
              })}
              {it.veg_manual_override && (
                <button type="button"
                        onClick={() => onEditItem(cat.id, it.id, { veg_manual_override: false })}
                        className="text-[10px] px-2 py-1 rounded-full border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
                        data-testid={`editor-item-veg-clear-${it.id}`}>
                  Clear override
                </button>
              )}
            </div>
          </div>
          <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-1">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!it.chef_recommend}
                     onChange={(e) => onEditItem(cat.id, it.id, { chef_recommend: e.target.checked })}
                     data-testid={`editor-item-recommend-${it.id}`} />
              Recommended (Chef&apos;s Pick)
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!it.bestseller}
                     onChange={(e) => onEditItem(cat.id, it.id, { bestseller: e.target.checked })}
                     data-testid={`editor-item-bestseller-${it.id}`} />
              Bestseller
            </label>
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={it.available !== false}
                     onChange={(e) => onEditItem(cat.id, it.id, {
                       available: e.target.checked,
                       unavailable_mode: e.target.checked ? null : (it.unavailable_mode || 'indefinite'),
                     })}
                     data-testid={`editor-item-enabled-${it.id}`} />
              Enabled (visible to guests)
            </label>
            <button type="button"
                    onClick={() => onOpenUnavailModal(it.id, it.name)}
                    className="ml-auto text-[10px] px-2 py-1 rounded-full border hover:bg-gray-50"
                    data-testid={`editor-item-soldout-${it.id}`}>
              Mark Sold Out (shift/indefinite)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ==================== Mark Unavailable modal ==================== */

function UnavailableModal({ itemName, onClose, onSubmit }) {
  const [mode, setMode] = useState('shift');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await onSubmit({ mode, reason: reason.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="unavail-modal">
        <DialogHeader>
          <DialogTitle style={{ color: PRIMARY }}>
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-600" />
              Mark &ldquo;{itemName}&rdquo; unavailable
            </span>
          </DialogTitle>
          <DialogDescription>
            The item will be hidden from the guest menu until restored.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Duration</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button type="button"
                      onClick={() => setMode('shift')}
                      data-testid="unavail-mode-shift"
                      className={`text-left rounded-lg border p-3 text-sm transition ${
                        mode === 'shift'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                <div className="font-medium">This shift only</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Auto-restores after 12 hours
                </div>
              </button>
              <button type="button"
                      onClick={() => setMode('indefinite')}
                      data-testid="unavail-mode-indefinite"
                      className={`text-left rounded-lg border p-3 text-sm transition ${
                        mode === 'indefinite'
                          ? 'border-rose-500 bg-rose-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                <div className="font-medium">Indefinite</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Stays hidden until manually restored
                </div>
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Reason (optional)</Label>
            <Input value={reason}
                   onChange={(e) => setReason(e.target.value.slice(0, 200))}
                   placeholder="e.g. Ingredient sold out"
                   data-testid="unavail-reason-input" />
            <div className="text-[11px] text-gray-400 text-right">
              {reason.length}/200
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  style={{ background: '#B91C1C', color: '#fff' }}
                  data-testid="unavail-confirm-btn">
            {saving ? 'Saving…' : 'Mark Unavailable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VegDot({ type }) {
  const color =
    type === 'veg' ? '#2D9C5B' :
    type === 'non_veg' ? '#C0392B' :
    type === 'egg' ? '#E0A73C' : '#B7B7B7';
  const label =
    type === 'veg' ? 'Veg' :
    type === 'non_veg' ? 'Non-veg' :
    type === 'egg' ? 'Egg' : '';
  if (!label) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide"
          title={label}>
      <span className="inline-block w-3 h-3 rounded-sm border" style={{ borderColor: color }}>
        <span className="block w-1.5 h-1.5 rounded-full m-[3px]" style={{ background: color }} />
      </span>
      {label}
    </span>
  );
}

/* QR dialog moved to /components/menu/QrDialog.jsx (ITER288) — reused by
   the Manager Menu Share flow. */
